import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTranslation as getTranslationHelper } from '../../utils/translationHelpers';
import { practiceTestReducer, initialState } from './PracticeTest.reducer';
import type { Question } from '../../types';
import { ProgressIndicator } from './PracticeTest.components';
import { PracticeTestHeaderControls } from './PracticeTest.headerControls';
import { PracticeLoadingScreen } from './PracticeLoadingScreen';
import {
  createHandleAnswer,
  createNextQuestion,
} from './PracticeTest.handlers';
import {
  useLoadQuestions,
  useBodyClass,
} from './PracticeTest.effects';
import {
  useGetTranslatedTestNameForId,
  useSupabaseRecommendationContext,
} from './PracticeTest.hooks';
import {
  getTestName,
  getNextTest as getNextTestUtil,
  calculateMotivationWord,
} from './PracticeTest.utils';
import {
  pickCorrectReply,
  pickIncorrectReply,
  getStreakMessage,
  type Lang,
} from './PracticeTest.replies';
import { aiCoach } from '../../services/aiCoach';
import { testCountTracker } from '../../services/testCountTracker';
import { isUserSubscribed, isAdminOverride, FREE_TOPICS } from '../../services/subscriptionGate';
import { getExamUnlockState } from '../../services/aiExamUnlockService';
import { HardGate } from '../HardGate/HardGate';
import { SoftGate } from '../SoftGate';
import { ExamDateModal } from '../ExamDateModal';
import { SituationModal } from '../SituationModal';
import { userAuth } from '../../services/userAuth';

import { PostTestFeedback } from '../PostTestFeedback/PostTestFeedback';

import { safeArrayAt } from '../../utils/safeRender';
import { ChatMessage } from '../ChatMessage';
import { preloadAnimationVideo, preloadAnimationsForQuestions } from './QuestionAnimationPlayer';
import { ProfessionalQuestionDisplay } from './ProfessionalQuestionDisplay';
import { assetPreloader } from '../../services/assetPreloader';
import { usePrefetchNextQuestions } from '../../hooks/usePrefetchNextQuestions';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { useNetworkAwareness } from '../../services/networkAwareLoading';
import '../PracticeTest.css';

const RESPONSE_POOL = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const;

function shuffle<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function pickRandomCorrect(t: (k: string) => string): string {
  const n = Math.floor(Math.random() * 8) + 1;
  const word = t(`practice.correct.${n}`);
  return word && !word.startsWith('practice.') ? word : 'Well done!';
}

function pickRandomIncorrect(t: (k: string) => string): string {
  const n = Math.floor(Math.random() * 8) + 1;
  const word = t(`practice.incorrect.${n}`);
  return word && !word.startsWith('practice.') ? word : 'Not quite';
}

export const PracticeTest: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const _location = useLocation();
  const { t_nested, currentLanguage } = useLanguage();

  const [state, dispatch] = useReducer(practiceTestReducer, initialState);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [showMoveOn, setShowMoveOn] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const [responseOptions, setResponseOptions] = useState<Array<{ key: string; label: string }> | null>(null);
  const [selectedResponse, setSelectedResponse] = useState<string | null>(null);
  const [coachReply, setCoachReply] = useState<string | null>(null);
  const [, setIsCoachLoading] = useState(false);
  const [showExamDateModal, setShowExamDateModal] = useState(false);

  const [showSituationModal, setShowSituationModal] = useState(false);
  const [showPostTestFeedback, setShowPostTestFeedback] = useState(false);
  const [showSoftGateAfterSecondTest, setShowSoftGateAfterSecondTest] = useState(false);
  const softGateSecondTestShownRef = useRef(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLElement>(null);
  const isNearBottomRef = useRef(true);
  const correctCardRef = useRef<HTMLDivElement>(null);
  const incorrectCardRef = useRef<HTMLDivElement>(null);
  const responseRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const motivationWordRef = useRef('');
  const streakCountRef = useRef<number>(0);
  const [streakMessage, setStreakMessage] = useState<string | null>(null);
  const previousAnswerCorrectRef = useRef<boolean | undefined>(undefined);
  const sessionFrozenRef = useRef(false);
  const isAliveRef = useRef(true);
  const stateRef = useRef(state);
  stateRef.current = state;
  const _questionReadSeqRef = useRef(0);
  const pillListenSeqRef = useRef(0);

  const runCoachAfterAnswerRef = useRef<
    (args: { isCorrect: boolean; answerId: string; currentQ: Question }) => void
  >(() => {});

  const onQuestionsLoaded = useCallback((loaded: Question[]) => {
    if (!isAliveRef.current) return;
    // Prioritize current/near-future media first; stagger the rest.
    preloadAnimationsForQuestions(loaded, { maxImmediate: 3, staggerMs: 180 });
    assetPreloader.preloadCriticalAssets(testId || '', loaded);
  }, [testId]);

  const currentQuestion = safeArrayAt(questions, state.currentQuestionIndex);
  const isLastQuestion = questions.length > 0 && state.currentQuestionIndex >= questions.length - 1;

  // Preload next question's animation video in background so it's ready instantly
  const upcomingQuestion = safeArrayAt(questions, state.currentQuestionIndex + 1);
  const nextAnimationId = (upcomingQuestion as { animationId?: string | null } | undefined)?.animationId;
  useEffect(() => {
    preloadAnimationVideo(nextAnimationId);
  }, [nextAnimationId]);

  // Phase 4a: Smart prefetch next 2-5 questions based on network speed
  usePrefetchNextQuestions({
    currentIndex: state.currentQuestionIndex,
    totalQuestions: questions.length,
    questions,
    enabled: true
  });

  // Phase 4e: Intersection Observer - prefetch when user is 80% through current section
  const _questionSectionRef = useIntersectionObserver(questions, state.currentQuestionIndex, {
    threshold: 0.8,
    prefetchDistance: 2
  });

  // Phase 4c: Network awareness for quality adjustment
  const { status: _networkStatus, qualityTier: _qualityTier, shouldLoadVideo: _shouldLoadVideo } = useNetworkAwareness();

  const getTranslatedTestNameForId = useGetTranslatedTestNameForId(
    currentLanguage || 'nl',
    t_nested
  );

  const supabaseContext = useSupabaseRecommendationContext(
    testId,
    state.testComplete,
    state.testComplete ? state.score : undefined,
    state.testComplete && questions.length > 0 ? questions.length : undefined
  );

  const getNextTest = useCallback(
    () =>
      getNextTestUtil(
        testId,
        state.precomputedNextTest,
        getTranslatedTestNameForId,
        t_nested,
        undefined,
        aiCoach,
        supabaseContext ?? undefined
      ),
    [testId, state.precomputedNextTest, getTranslatedTestNameForId, t_nested, supabaseContext]
  );

  const navigateToNextTest = useCallback(
    (id: string) => {
      navigate(`/practice/${id}`, { replace: true });
    },
    [navigate]
  );

  const calculateMotivationWordWrapper = useCallback(
    (isCorrect: boolean) => calculateMotivationWord(isCorrect, t_nested),
    [t_nested]
  );

  const nextQuestionRef = useRef<() => void>(() => {});
  const nextQuestion = useCallback(() => {
    nextQuestionRef.current();
  }, []);

  const baseHandlersContext = useMemo(
    () => ({
      state,
      dispatch,
      questions,
      testId,
      currentLanguage: currentLanguage || 'nl',
      t_nested,
      chatMessages: state.chatMessages,
      motivationWordRef,
      previousAnswerCorrectRef,
      nextQuestion,
      getNextTest,
      navigateToNextTest,
      getTestName: () => getTestName(testId, t_nested, getTranslatedTestNameForId),
      calculateMotivationWordWrapper,
      handleAnswer: (() => {}) as (answerId: string) => void,
      isMuted: state.isMuted,
      questionStartTime: state.questionStartTime,
      recentPerformance: state.recentPerformance,
      currentQuestionIndex: state.currentQuestionIndex,
      nextTest: getNextTest() || { id: '', name: '' },
      runCoachAfterAnswerRef,
    }),
    [
      state,
      questions,
      testId,
      currentLanguage,
      t_nested,
      nextQuestion,
      getNextTest,
      navigateToNextTest,
      calculateMotivationWordWrapper,
      getTranslatedTestNameForId,
    ]
  );

  const nextQuestionImpl = useMemo(
    () => createNextQuestion(baseHandlersContext),
    [baseHandlersContext]
  );
  nextQuestionRef.current = nextQuestionImpl;

  const handleAnswer = useMemo(() => {
    const ctx = { ...baseHandlersContext, handleAnswer: (() => {}) as (answerId: string) => void };
    return createHandleAnswer(ctx);
  }, [baseHandlersContext]);

  const handleAnswerRef = useRef(handleAnswer);
  handleAnswerRef.current = handleAnswer;
  const onAnswerStable = useCallback((answerId: string) => {
    handleAnswerRef.current(answerId);
  }, []);

  const handlersContext = useMemo(
    () => ({
      ...baseHandlersContext,
      handleAnswer,
    }),
    [baseHandlersContext, handleAnswer]
  );

  const effectsContext = useMemo(
    () => ({
      ...handlersContext,
      setQuestions,
      sessionFrozenRef,
      isAliveRef,
      setExplanationInChat: () => {},
      getTranslatedTestNameForId,
      wrongAnswersInRowRef: { current: 0 },
      recentMistakesRef: { current: 0 },
      recentMistakesInTopicRef: { current: {} },
      caseRuleMistakeCountsRef: { current: {} },
      caseRuleAddressedRef: { current: {} },
      caseCoachingRef: {
        current: { activeQuestionId: null, ruleText: '', proceedText: '' },
      },
      lastCheckInTimeRef: { current: 0 },
      spokenQuestionsRef: { current: new Set<number>() },
      selectedAnswer: state.selectedAnswer,
      isAnswered: state.isAnswered,
      showExplanation: state.showExplanation,
      onQuestionsLoaded,
    }),
    [handlersContext, getTranslatedTestNameForId, state.selectedAnswer, state.isAnswered, state.showExplanation, onQuestionsLoaded]
  );

  useLoadQuestions(effectsContext);
  useBodyClass();

  useEffect(() => {
    isAliveRef.current = true;
    return () => {
      isAliveRef.current = false;
    };
  }, []);

  const handleExit = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  const _advanceAndReset = useCallback(() => {
    setShowMoveOn(false);
    setShowFooter(false);
    setResponseOptions(null);
    setSelectedResponse(null);
    setCoachReply(null);
    nextQuestion();
  }, [nextQuestion]);

  const handleContinueOrFinish = useCallback(() => {
    setShowMoveOn(false);
    setShowFooter(false);
    if (isLastQuestion) {
      nextQuestion();
    } else {
      setResponseOptions(null);
      setSelectedResponse(null);
      setCoachReply(null);
      nextQuestion();
    }
  }, [nextQuestion, isLastQuestion]);


  const handleResponseSelect = useCallback(
    (optionKey: string, optionText: string) => {
      pillListenSeqRef.current += 1;
      setResponseOptions(null);
      setSelectedResponse(optionText);
      const lang = (currentLanguage || 'nl') as Lang;
      const correctOpt = currentQuestion?.options?.find(
        (o) => o.id === currentQuestion?.correctAnswerId
      );
      const correctAnswerText = correctOpt?.text ?? '';
      const reply = pickIncorrectReply(correctAnswerText, lang);
      if (isAliveRef.current) {
        setCoachReply(reply.text);
        setShowMoveOn(true);
        setShowFooter(true);
        setIsCoachLoading(false);

      }
    },
    [currentQuestion, currentLanguage]
  );

  runCoachAfterAnswerRef.current = ({
    isCorrect,
    currentQ: _currentQ,
  }: {
    isCorrect: boolean;
    answerId: string;
    currentQ: Question;
    muted: boolean;
  }) => {
    if (testId === 'mock-exam') return;
    const lang = (currentLanguage || 'nl') as Lang;
    if (isCorrect) {
      const reply = pickCorrectReply(lang);
      motivationWordRef.current = reply.text;
      streakCountRef.current += 1;
      const msg = getStreakMessage(streakCountRef.current, lang);
      if (msg) setStreakMessage(msg);
      setShowMoveOn(true);
      setShowFooter(true);
    } else {
      streakCountRef.current = 0;
      setStreakMessage(null);
      const pool = shuffle([...RESPONSE_POOL]);
      const [a, b] = pool.slice(0, 2);
      const wrongResponseOpts = [
        { key: a, label: t_nested(`practice.response.${a}`) },
        { key: b, label: t_nested(`practice.response.${b}`) },
      ];
      setResponseOptions(wrongResponseOpts);
      setShowFooter(false);
    }
  };

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        chatEndRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest',
        });
      });
    });
  }, []);

  /* Track if user is near bottom — only auto-scroll when they are (Cursor-style) */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
      isNearBottomRef.current = dist < 150;
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSkipCorrect = useCallback(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  const handleSkipIncorrect = useCallback(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  const handleRetake = useCallback(() => {
    if (!isAdminOverride() && !isUserSubscribed() && testCountTracker.getTestCount() >= 3) {
      if (testCountTracker.getFreeAiExamPending()) {
        navigate('/ai-exam?skipIntro=true', { replace: true });
        return;
      }
      dispatch({ type: 'SET_HARD_GATE', payload: true });
      return;
    }
    // Reset all local UI state
    setShowMoveOn(false);
    setShowFooter(false);
    setResponseOptions(null);
    setSelectedResponse(null);
    setCoachReply(null);
    setStreakMessage(null);
    if (streakCountRef.current !== undefined) {
      streakCountRef.current = 0;
    }
    setQuestions([]);
    sessionFrozenRef.current = false;
    dispatch({ type: 'RESET_SESSION' });
  }, [dispatch, navigate]);

  const handleNextTest = useCallback(() => {
    if (!isAdminOverride() && !isUserSubscribed() && testCountTracker.getTestCount() >= 3) {
      if (testCountTracker.getFreeAiExamPending()) {
        navigate('/ai-exam', { replace: true });
        return;
      }
      dispatch({ type: 'SET_HARD_GATE', payload: true });
      return;
    }
    // Free users with < 3 tests: guaranteed order (traffic-lights-signals → road-signs → priority-rules)
    const testsCompleted = testCountTracker.getTestCount();
    if (!isAdminOverride() && !isUserSubscribed() && testsCompleted < 3) {
      const nextTestId = FREE_TOPICS[testsCompleted];
      if (nextTestId) {
        sessionFrozenRef.current = false;
        setShowMoveOn(false);
        setShowFooter(false);
        setResponseOptions(null);
        setSelectedResponse(null);
        setCoachReply(null);
        setStreakMessage(null);
        if (streakCountRef.current !== undefined) {
          streakCountRef.current = 0;
        }
        setQuestions([]);
        dispatch({ type: 'RESET_SESSION' });
        navigate(`/practice/${nextTestId}`, { replace: true });
        return;
      }
    }
    const next = getNextTest();
    if (next?.id) {
      sessionFrozenRef.current = false;
      setShowMoveOn(false);
      setShowFooter(false);
      setResponseOptions(null);
      setSelectedResponse(null);
      setCoachReply(null);
      setStreakMessage(null);
      if (streakCountRef.current !== undefined) {
        streakCountRef.current = 0;
      }
      setQuestions([]);
      dispatch({ type: 'RESET_SESSION' });
      navigate(`/practice/${next.id}`, { replace: true });
    } else {
      navigate('/dashboard');
    }
  }, [getNextTest, navigate, dispatch]);

  /* Cursor-style: scroll to bottom only when user is near bottom (lets them scroll up freely) */
  useEffect(() => {
    if (!state.isAnswered && !state.testComplete) return;
    if (!isNearBottomRef.current) return;
    const id = setTimeout(() => scrollToBottom(), 250);
    return () => clearTimeout(id);
  }, [state.isAnswered, state.testComplete, coachReply, showMoveOn, showFooter, responseOptions, scrollToBottom]);

  // Ensure Continue CTA + disclaimer are brought into the viewport on mobile when they appear.
  useEffect(() => {
    if (!showFooter || state.testComplete) return;
    const footerEl = footerRef.current;
    if (!footerEl) return;
    const id = requestAnimationFrame(() => {
      footerEl.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest',
      });
    });
    return () => cancelAnimationFrame(id);
  }, [showFooter, state.testComplete, coachReply, showMoveOn]);

  /* Scroll to top on new question */
  useEffect(() => {
    scrollRef.current && (scrollRef.current.scrollTop = 0);
    setStreakMessage(null);
  }, [state.currentQuestionIndex]);



  /* Persist results topic for OAuth return (SoftGate / RegistrationModal). */
  useEffect(() => {
    if (!state.testComplete || !testId || testId === 'mock-exam' || testId.includes('mock')) return;
    try {
      localStorage.setItem('currentResultsTestId', testId);
    } catch {
      /* ignore */
    }
  }, [state.testComplete, testId]);

  /* Unauthenticated users: SoftGate after the 2nd completed practice (free tier count). */
  useEffect(() => {
    if (!state.testComplete) {
      softGateSecondTestShownRef.current = false;
      return;
    }
    if (!testId || testId === 'mock-exam' || testId.includes('mock')) return;
    if (userAuth.isAuthenticated()) return;
    const testsCompleted = testCountTracker.getTestCount();
    // Strictly show on the 2nd completion (free tier), once per completion render.
    if (testsCompleted !== 2) return;
    if (softGateSecondTestShownRef.current) return;
    softGateSecondTestShownRef.current = true;
    setShowSoftGateAfterSecondTest(true);
  }, [state.testComplete, testId]);

  /* Show exam date modal after test 2 completion; if already skipped/set, show PostTestFeedback directly */
  useEffect(() => {
    if (state.testComplete && !showExamDateModal && testCountTracker.getTestCount() === 2) {
      // Guests: after test 2 we only show SoftGate (no other popups).
      if (!userAuth.isAuthenticated()) return;
      const examDateSkipped = localStorage.getItem('examDateSkipped') === 'true';
      const examDateExists = localStorage.getItem('userExamDate');
      if (!examDateSkipped && !examDateExists) {
        setShowExamDateModal(true);
      } else {
        setShowPostTestFeedback(true);
      }
    }
  }, [state.testComplete, showExamDateModal]);

  /* Show situation modal after test 8 completion */
  useEffect(() => {
    if (state.testComplete && !showSituationModal && testCountTracker.getTestCount() === 8) {
      const situationAtTest8 = localStorage.getItem('situationAtTest8');
      const situationSkipped = localStorage.getItem('situationTest8Skipped') === 'true';
      if (!situationAtTest8 && !situationSkipped) {
        setShowSituationModal(true);
      }
    }
  }, [state.testComplete, showSituationModal]);


  // Gate: admin first, then subscribed, then free tier or topic lock.
  const testsCompleted = testCountTracker.getTestCount();
  const topicLocked = testId && !FREE_TOPICS.includes(testId);
  const freeTierUsed = testsCompleted >= 3;
  const isBlocked =
    !state.testComplete &&
    !isAdminOverride() &&
    !isUserSubscribed() &&
    (freeTierUsed || topicLocked);
  const shouldShowHardGate = state.showHardGate || isBlocked;

  if (!testId) {
    return null;
  }

  if (questions.length === 0 && !state.testComplete && testId) {
    return (
      <>
        {shouldShowHardGate && <HardGate />}
        <div
          className="practice-test-root practice-test safe-top"
          style={{
            position: 'relative',
            height: '100dvh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            background: '#0F172A',
          }}
        >
          <PracticeLoadingScreen />
        </div>
      </>
    );
  }

  const correctOpt = currentQuestion?.options?.find(
    (o) => o.id === currentQuestion?.correctAnswerId
  );
  const correctLine = correctOpt
    ? `${String.fromCharCode(65 + (currentQuestion?.options?.indexOf(correctOpt) ?? 0))} - ${correctOpt.text}`
    : '';

  // RTL (Arabic): keep numeric fractions stable (avoid "10/2" visual flip).
  const Fraction = ({ a, b }: { a: number; b: number }) => (
    <span dir="ltr" style={{ unicodeBidi: 'isolate', display: 'inline-flex', alignItems: 'baseline' }}>
      <span className="score-number" style={{ color: '#1A3E7A' }}>{a}</span>
      <span className="score-separator" style={{ color: '#1A3E7A' }}>/</span>
      <span className="score-total" style={{ color: '#1A3E7A' }}>{b}</span>
    </span>
  );

  return (
    <>
      {shouldShowHardGate && <HardGate />}

      {showSoftGateAfterSecondTest && (
        <SoftGate
          onDismiss={() => {
            setShowSoftGateAfterSecondTest(false);
            userAuth.refreshUser();
          }}
          nextTestId={getNextTest()?.id}
        />
      )}

      <ExamDateModal
        isOpen={showExamDateModal}
        onClose={() => {
          setShowExamDateModal(false);
          if (testCountTracker.getTestCount() === 2) setShowPostTestFeedback(true);
        }}
        onSave={() => {
          setShowExamDateModal(false);
          if (testCountTracker.getTestCount() === 2) setShowPostTestFeedback(true);
        }}
      />
      <SituationModal
        isOpen={showSituationModal}
        onClose={() => setShowSituationModal(false)}
        onSave={(goal) => {
          if (goal) {
            localStorage.setItem('situationAtTest8', goal);
          } else {
            localStorage.setItem('situationTest8Skipped', 'true');
          }
          setShowSituationModal(false);
        }}
      />
      {(() => {
        const fbTotal = state.session?.sessionQuestions?.length ?? questions.length ?? 10;
        const fbCorrect = (() => {
          const sc = state.session?.answers?.filter((a) => a.isCorrect).length ?? 0;
          return (sc > 0 && state.session?.answers && state.session.answers.length >= fbTotal) ? sc : (state.score ?? 0);
        })();
        const testNum = testCountTracker.getTestCount() as 2 | 3;
        return (
          <PostTestFeedback
            isOpen={showPostTestFeedback}
            onClose={() => setShowPostTestFeedback(false)}
            testNumber={testNum}
            correct={fbCorrect}
            total={fbTotal}
          />
        );
      })()}
    <div className="practice-test-root practice-test safe-top">
      <header
        className="practice-chat-header"
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          minHeight: 48,
        }}
      >
        <div className="practice-header-progress-slot">
          <ProgressIndicator
            currentQuestionIndex={state.currentQuestionIndex}
            totalQuestions={questions.length}
            questionSubject={testId || ''}
            t_nested={t_nested}
            getTranslationHelper={getTranslationHelper}
            getTranslatedTestNameForId={getTranslatedTestNameForId}
          />
        </div>
        <div
          className="practice-header-right-cluster"
          style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}
        >
          <div className="practice-live-indicator">
            <span className="practice-live-dot" aria-hidden="true" />
            <span className="practice-live-text">{getTranslationHelper(t_nested, 'practice.live', 'مباشر')}</span>
          </div>
          <PracticeTestHeaderControls
            onExit={handleExit}
            dispatch={dispatch}
          />
        </div>
      </header>

      <div
        ref={scrollRef}
        className="practice-test-scroll"
        role="region"
        aria-label="Chat messages"
        tabIndex={0}
      >
        <div className="practice-test-chat-content">
          <div className="practice-content-wrapper">
          {/* Professional CBR Layout: Media-First or Text-Only */}
          <ProfessionalQuestionDisplay
            currentQuestion={currentQuestion}
            questionNumber={state.currentQuestionIndex + 1}
            selectedAnswer={state.selectedAnswer}
            isAnswered={state.isAnswered}
            onAnswer={onAnswerStable}
            streakMessage={streakMessage}
          />

          {state.isAnswered && currentQuestion && (
            <>
              {state.selectedAnswer === currentQuestion.correctAnswerId ? (
                <div ref={correctCardRef} style={{ marginTop: 24 }}>
                  <ChatMessage
                    role="coach"
                    content={motivationWordRef.current || pickRandomCorrect(t_nested)}
                  />
                  <button
                    type="button"
                    className="skip-explanation-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSkipCorrect();
                    }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, marginTop: 8 }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div ref={incorrectCardRef} style={{ marginTop: 24 }}>
                  <ChatMessage
                    role="coach"
                    content={`${pickRandomIncorrect(t_nested)}\n\n${getTranslationHelper(t_nested, 'practice.correctAnswer', 'The correct answer is')} ${correctLine}\n\n${getTranslationHelper(t_nested, 'practice.reflective', 'What made you choose this?')}`}
                  />
                  <button
                    type="button"
                    className="skip-explanation-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSkipIncorrect();
                    }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, marginTop: 8 }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                </div>
              )}

              {responseOptions && responseOptions.length > 0 && (
                <div ref={responseRef} className="practice-response-block" style={{ marginTop: 16 }}>
                  <div className="practice-response-options">
                  {responseOptions.map((opt, idx) => (
                    <button
                      key={opt.key}
                      type="button"
                      className="practice-response-pill practice-nav-btn secondary"
                      onClick={() => handleResponseSelect(opt.key, opt.label)}
                    >
                      {String.fromCharCode(65 + idx)}. {opt.label}
                    </button>
                  ))}
                  </div>
                </div>
              )}

              {selectedResponse && (
                <div style={{ marginTop: 16 }}>
                  <ChatMessage
                    role="user"
                    content={selectedResponse}
                  />
                </div>
              )}

              {coachReply && (
                <div style={{ marginTop: 16 }}>
                  <ChatMessage
                    role="coach"
                    content={coachReply}
                  />
                </div>
              )}
            </>
          )}

          {state.testComplete && (() => {
            const total = state.session?.sessionQuestions?.length ?? questions.length ?? 10;
            const sessionCorrect = state.session?.answers?.filter((a) => a.isCorrect).length ?? 0;
            const correct = (sessionCorrect > 0 && state.session?.answers && state.session.answers.length >= total)
              ? sessionCorrect
              : (state.score ?? 0);
            const percent = total > 0 ? Math.round((correct / total) * 100) : 0;
            const previousScore = supabaseContext?.topicScores?.find((t) => t.topicId === testId)?.accuracy ?? 0;
            const justMastered = percent >= 70 && previousScore < 70;
            const masteryKey = testId ? `mastered_${testId}` : '';
            const alreadyShown = masteryKey ? localStorage.getItem(masteryKey) : null;
            const showMasteryCard = justMastered && masteryKey && !alreadyShown;
            if (showMasteryCard && masteryKey) {
              try { localStorage.setItem(masteryKey, 'true'); } catch { /* ignore */ }
            }
            const topicDisplayName = testId ? getTranslatedTestNameForId(testId) : '';
            const getResultsMessage = (pct: number): string => {
              if (pct === 100) return getTranslationHelper(t_nested, 'results.perfect', 'Perfect score!');
              if (pct >= 80) return getTranslationHelper(t_nested, 'results.great', 'Great work!');
              if (pct >= 60) return getTranslationHelper(t_nested, 'results.good', 'Good effort!');
              if (pct >= 40) return getTranslationHelper(t_nested, 'results.keep', 'Keep practising.');
              return getTranslationHelper(t_nested, 'results.retry', 'Try again. You can do this.');
            };
            return (
              <div ref={resultsRef} className="results-outermost-container" style={{ marginTop: 24 }}>
                {showMasteryCard && (
                  <ChatMessage
                    role="coach"
                    content={
                      <>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>
                          {getTranslationHelper(t_nested, 'results.mastered', '{topic} mastered.').replace('{topic}', topicDisplayName)}
                        </div>
                        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>
                          {getTranslationHelper(t_nested, 'results.masteredSub', 'Well done, keep it up.')}
                        </div>
                      </>
                    }
                  />
                )}
                <ChatMessage
                  role="coach"
                  className="practice-results-message"
                  content={
                    <div className="results-inner practice-results-in-chat">
                      <div className="practice-test-results-divider" />
                      <h3 className="practice-results-title" style={{ margin: '0 0 0.5rem', fontSize: 'var(--text-base)', fontWeight: 700, color: '#1A3E7A' }}>
                        {getTranslationHelper(t_nested, 'results.title', 'Practice complete')}
                      </h3>
                      <div className="result-score" style={{ justifyContent: 'center', margin: '0.5rem 0' }}>
                        <Fraction a={correct} b={total} />
                        <span className="score-percent" style={{ marginInlineStart: '0.5rem', color: '#1A3E7A' }}>{percent}%</span>
                      </div>
                      <p
                        className="practice-test-results-motivation"
                        style={{ textAlign: 'center', fontWeight: 400, color: '#6b7280', margin: '0.5rem 0 1rem', fontSize: 'var(--text-sm)' }}
                      >
                        {getResultsMessage(percent)}
                      </p>
                      <div className="practice-test-results-divider" />
                      <div className="practice-test-results-ctas" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {(() => {
                          const freeAiExamReady =
                            !isUserSubscribed() &&
                            !isAdminOverride() &&
                            testCountTracker.getTestCount() >= 3 &&
                            testCountTracker.getFreeAiExamPending();
                          const subscribedAiExamReady =
                            isUserSubscribed() &&
                            !isAdminOverride() &&
                            getExamUnlockState().activeExamInstanceId !== null;
                          const showAiExamCTA = freeAiExamReady || subscribedAiExamReady;
                          const aiExamLabel =
                            currentLanguage === 'nl'
                              ? 'AI-examen'
                              : currentLanguage === 'ar'
                                ? 'الاختبار الذكي'
                                : 'AI Exam';
                          const readyBadge =
                            currentLanguage === 'nl'
                              ? 'Klaar'
                              : currentLanguage === 'ar'
                                ? 'جاهز'
                                : 'Ready';
                          const retakeLabel =
                            currentLanguage === 'nl'
                              ? 'Opnieuw'
                              : currentLanguage === 'ar'
                                ? 'إعادة'
                                : 'Retake';
                          if (showAiExamCTA) {
                            return (
                              <>
                                <div className="ai-exam-cta-wrapper" style={{ width: '100%' }}>
                                  <span className="ai-exam-ready-badge">{readyBadge}</span>
                                  <button
                                    type="button"
                                    className="practice-nav-btn primary"
                                    onClick={() => navigate('/ai-exam')}
                                    style={{ width: '100%', background: '#1A3E7A', color: 'white' }}
                                  >
                                    {aiExamLabel}
                                  </button>
                                </div>
                                <button
                                  type="button"
                                  className="practice-nav-btn secondary"
                                  onClick={handleRetake}
                                  style={{ width: '100%' }}
                                >
                                  {retakeLabel}
                                </button>
                              </>
                            );
                          }
                          return (
                            <>
                              <button
                                type="button"
                                className="practice-nav-btn primary"
                                onClick={handleNextTest}
                                style={{ width: '100%' }}
                              >
                                {getTranslationHelper(t_nested, 'results.nextTest', 'Next test')}
                              </button>
                              <button
                                type="button"
                                className="practice-nav-btn secondary"
                                onClick={handleRetake}
                                style={{ width: '100%' }}
                              >
                                {retakeLabel}
                              </button>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  }
                />
              </div>
            );
          })()}
          <div ref={chatEndRef} className="practice-test-scroll-anchor" />
          </div>
        </div>
      </div>
      {showFooter && !state.testComplete && (
        state.selectedAnswer === currentQuestion?.correctAnswerId ||
        (coachReply && showMoveOn)
      ) && (
        /* One stable bottom footer: continue CTA, then disclaimer. */
        <footer ref={footerRef} className="practice-test-footer practice-footer">
          <div className="practice-cta-wrap">
            <button
              type="button"
              className="practice-continue-btn practice-nav-btn primary"
              onClick={handleContinueOrFinish}
            >
              {isLastQuestion
                ? getTranslationHelper(t_nested, 'practice.finish', 'Finish')
                : getTranslationHelper(t_nested, 'practice.continue', 'Continue')}
            </button>
          </div>
          <p className="practice-disclaimer">
            {getTranslationHelper(t_nested, 'practice.disclaimer', 'TheoryCoach can make mistakes.')}
          </p>
        </footer>
      )}
    </div>
    </>
  );
};
